import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { authorize } from '../../services/firebase'
import { AuthContext, AuthStatusContext } from '../../context/firebase-context'
import { DEFAULT_REDIRECT_PATH, getSafeRedirectPath } from '../../common/safe-redirect-path'
import Preloader from '../../components/Preloader/Preloader'
import AuthFailureScreen from '../../components/AuthFailureScreen/AuthFailureScreen'
import { brandLoginUrl } from '../../common/brand-login'
const FIREBASE_AUTH_STORAGE_PREFIX = 'firebase:authUser:'

const hasAuthenticatedUser = (user) => {
  return !!(user?.uid || user?.userUuid || user?.email || user?.idToken)
}

const importFirebaseAuthState = (searchParams) => {
  const key = searchParams.get('k')
  const value = searchParams.get('v')
  if (!key || !value || !key.startsWith(FIREBASE_AUTH_STORAGE_PREFIX)) {
    return false
  }
  // eslint-disable-next-line no-undef
  if (localStorage.getItem(key)) {
    return false
  }
  // eslint-disable-next-line no-undef
  localStorage.setItem(key, value)
  return true
}

// The brand login hands the visitor back to `path` (via `utm`) once signed in.
const redirectToWebLogin = (path = DEFAULT_REDIRECT_PATH) => {
  window.location.href = brandLoginUrl(path === DEFAULT_REDIRECT_PATH ? undefined : path)
}

const VerifyContainer = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const user = useContext(AuthContext)
  const { hydrateFailed } = useContext(AuthStatusContext)
  const redirectPath = useMemo(() => getSafeRedirectPath(searchParams.get('r')), [searchParams])
  // Firebase is signed in but the account didn't load: `redirectPath` would
  // send the visitor back to a guard that sends them here (NOLAN-STUDIO-22W).
  const accountFailed = hydrateFailed && !hasAuthenticatedUser(user)

  const verifyUser = useCallback(async () => {
    if (accountFailed) return

    if (hasAuthenticatedUser(user)) {
      navigate(redirectPath, { replace: true })
      return
    }

    if (importFirebaseAuthState(searchParams)) {
      navigate(0)
      return
    }

    const auth = await authorize()
    if (auth.currentUser) {
      navigate(redirectPath, { replace: true })
      return
    }

    let resolved = false
    const timeout = setTimeout(() => {
      if (!resolved) {
        redirectToWebLogin(redirectPath)
      }
    }, 5000)

    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      resolved = true
      clearTimeout(timeout)
      if (authUser) {
        navigate(redirectPath, { replace: true })
        return
      }
      redirectToWebLogin(redirectPath)
    })

    return () => {
      clearTimeout(timeout)
      unsubscribe && unsubscribe()
    }
  }, [accountFailed, navigate, redirectPath, searchParams, user])

  useEffect(() => {
    let disposed = false
    let cleanup
    verifyUser().then((nextCleanup) => {
      if (disposed) {
        nextCleanup && nextCleanup()
        return
      }
      cleanup = nextCleanup
    }).catch(() => {
      redirectToWebLogin(redirectPath)
    })
    return () => {
      disposed = true
      cleanup && cleanup()
    }
  }, [verifyUser])

  if (accountFailed) return <AuthFailureScreen returnTo={redirectPath} />

  return (
    <Preloader />
  )
}

export default VerifyContainer
